import React, { useState, useEffect, useRef, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import turfCircle from '@turf/circle';
import { geometryAreaSqMeters, formatArea } from '../services/spatial/geometryBudget';
import { simulateContractFunction, type SimulateResult } from '../v2/services/geolinkRulesService';
import { iso3ToIso2 } from '../countryCodes';
import {
  getRuleAuthorizations,
  type RuleAuthorization,
} from '../v2/services/ruleAuthorization';

// ─── Types ────────────────────────────────────────────────────────────────────
interface ExecutionRule {
  id: number;
  rule_name: string;
  rule_type: 'location' | 'proximity' | 'geofence';
  contract_id: number;
  contract_name?: string;
  contract_address?: string;
  function_name: string;
  center_latitude?: number;
  center_longitude?: number;
  radius_meters?: number;
  trigger_on?: 'enter' | 'exit' | 'dwell';
  auto_execute?: boolean;
  is_active?: boolean;
  requires_confirmation?: boolean;
  /** Legacy / fallback owner indicators — see `created_by_public_key` for the
   *  canonical multi-tenant ownership field per
   *  docs/geolink-multi-tenant-scoping-response.md §4. */
  public_key?: string;
  wallet_public_key?: string;
  /** Wallet that authored the rule (multi-tenant ownership column added in
   *  GeoLink migration 023). Populated on every fresh row; backfilled rows
   *  may carry the admin sentinel. Source of truth for `isOwnRule()`. */
  created_by_public_key?: string | null;
  /** JOINed `users.public_key` of the row's `user_id`. Returned by GeoLink's
   *  authenticated `/api/contracts/rules`. Treated as a secondary signal
   *  for ownership when `created_by_public_key` is null. */
  owner_public_key?: string | null;
  created_at?: string;
  function_parameters?: Record<string, any>;
  max_executions_per_public_key?: number;
  execution_time_window_seconds?: number;
  min_location_duration_seconds?: number;
  submit_readonly_to_ledger?: boolean;
  auto_deactivate_on_balance_threshold?: boolean;
  balance_threshold_xlm?: number;
  balance_check_asset_address?: string;
  use_smart_wallet_balance?: boolean;
  target_wallet_public_key?: string;
  required_wallet_public_keys?: string[];
  minimum_wallet_count?: number;
  quorum_type?: 'any' | 'all';
  geofence_id?: number;
  geojson_geometry?: Record<string, any>;
}

interface Contract {
  id: number;
  contract_address: string;
  contract_name: string;
  discovered_functions?: any[];
  /** Soft-delete flag. We send `?is_active=all` on /api/contracts so
   *  inactive parents come back too (otherwise rules anchored to a
   *  soft-deleted contract would silently lose their parent context
   *  in the UI). When this is `false`, the rule cards under this
   *  contract render the amber "⏸ contract paused" badge. */
  is_active?: boolean;
  /** Multi-tenant ownership column (GeoLink migration 023). When the
   *  connected wallet doesn't match this, the contract is shown read-only
   *  in the dropdown with a subtle "shared" hint per the agreed UX. */
  created_by_public_key?: string | null;
  owner_public_key?: string | null;
}

interface RegisterContractForm {
  address: string;
  name: string;
  network: 'testnet' | 'mainnet';
}

interface RuleForm {
  contract_id: string;
  rule_name: string;
  rule_type: 'location' | 'proximity' | 'geofence';
  center_latitude: string;
  center_longitude: string;
  radius_meters: string;
  function_name: string;
  function_parameters: string;
  // Advanced
  trigger_on: 'enter' | 'exit' | 'dwell';
  auto_execute: boolean;
  requires_confirmation: boolean;
  target_wallet_public_key: string;
  required_wallet_public_keys: string;
  minimum_wallet_count: string;
  quorum_type: 'any' | 'all';
  max_executions_per_public_key: string;
  execution_time_window_seconds: string;
  min_location_duration_seconds: string;
  submit_readonly_to_ledger: boolean;
  auto_deactivate_on_balance_threshold: boolean;
  balance_threshold_xlm: string;
  balance_check_asset_address: string;
  use_smart_wallet_balance: boolean;
  // geofence-specific
  geofence_input_mode: 'country' | 'id' | 'geojson';
  geofence_id: string;
  geojson_geometry: string;
  selected_country_name: string;
}

const DEFAULT_FORM: RuleForm = {
  contract_id: '',
  rule_name: '',
  rule_type: 'location',
  center_latitude: '',
  center_longitude: '',
  radius_meters: '500',
  function_name: '',
  function_parameters: '{}',
  trigger_on: 'enter',
  auto_execute: false,
  requires_confirmation: true,
  target_wallet_public_key: '',
  required_wallet_public_keys: '',
  minimum_wallet_count: '',
  quorum_type: 'any',
  max_executions_per_public_key: '',
  execution_time_window_seconds: '',
  min_location_duration_seconds: '',
  submit_readonly_to_ledger: false,
  auto_deactivate_on_balance_threshold: false,
  balance_threshold_xlm: '',
  balance_check_asset_address: '',
  use_smart_wallet_balance: false,
  geofence_input_mode: 'country',
  geofence_id: '',
  geojson_geometry: '',
  selected_country_name: '',
};

const STEPS = ['Details', 'Location', 'Function', 'Advanced', 'Review'];

// ─── GeoJSON circle (true geodesic via @turf/circle) ──────────────────────────
// The old hand-rolled version approximated a circle with an equirectangular
// dx/dy scaling that skews badly at high latitudes; @turf/circle draws a
// proper great-circle buffer so the preview matches GeoLink's evaluation.
function makeCircleGeoJSON(lng: number, lat: number, radiusMeters: number, pts = 64) {
  const feature = turfCircle([lng, lat], radiusMeters / 1000, {
    steps: pts,
    units: 'kilometers',
  });
  return {
    type: 'FeatureCollection' as const,
    features: [feature],
  };
}

// ─── Shared style tokens — light theme matching GeoLink / country overlays ────
const C = {
  text: '#333',
  textMuted: '#555',
  textFaint: '#888',
  border: '#ddd',
  borderFaint: '#eee',
  inputBg: '#fff',
  selectBg: '#fff',
  cardBg: '#f8f9fa',
  wizardBg: '#fff',
  indigo: '#FFD700',       // primary accent = GeoLink gold
  indigoLight: '#996600',  // dark gold for text on white
  green: '#28a745',
  red: '#dc3545',
  amber: '#fd7e14',
};

const S = {
  wrap: { padding: '0 2px' } as React.CSSProperties,

  stepperWrap: (h: boolean): React.CSSProperties => ({
    display: 'flex', flexDirection: h ? 'row' : 'column',
    alignItems: h ? 'center' : 'flex-start',
    gap: h ? 0 : '4px', marginBottom: '20px',
  }),
  stepItem: (h: boolean): React.CSSProperties => ({
    display: 'flex', flexDirection: h ? 'column' : 'row',
    alignItems: 'center', gap: h ? '5px' : '10px',
    flex: h ? 1 : undefined,
  }),
  stepDot: (active: boolean, done: boolean): React.CSSProperties => ({
    width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '12px', fontWeight: 700, transition: 'all 0.2s',
    background: done ? 'rgba(40,167,69,0.12)' : active ? C.indigo : '#f0f0f0',
    color: done ? C.green : active ? '#000' : C.textFaint,
    border: `2px solid ${done ? C.green : active ? C.indigo : '#ddd'}`,
    boxShadow: active ? '0 0 8px rgba(255,215,0,0.5)' : 'none',
  }),
  stepLabel: (active: boolean, done: boolean): React.CSSProperties => ({
    fontSize: '11px', fontWeight: active ? 700 : 400, whiteSpace: 'nowrap' as const,
    color: active ? C.indigoLight : done ? C.green : C.textFaint,
  }),
  connector: (h: boolean): React.CSSProperties => ({
    flex: h ? 1 : undefined, height: h ? '2px' : undefined,
    width: h ? undefined : '2px', alignSelf: h ? undefined : 'stretch',
    minWidth: h ? '12px' : undefined, minHeight: h ? undefined : '14px',
    background: '#e0e0e0', margin: h ? '0 4px' : '0 14px',
  }),

  field: { marginBottom: '14px' } as React.CSSProperties,
  label: { display: 'block', fontSize: '12px', color: C.textMuted, marginBottom: '5px', fontWeight: 600, letterSpacing: '0.3px' } as React.CSSProperties,
  hint: { fontSize: '11px', color: C.textFaint, marginTop: '4px' } as React.CSSProperties,
  input: {
    width: '100%', boxSizing: 'border-box' as const,
    background: C.inputBg, border: `1px solid ${C.border}`,
    borderRadius: '7px', color: C.text, padding: '9px 11px', fontSize: '13px',
    outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
  } as React.CSSProperties,
  select: {
    width: '100%', boxSizing: 'border-box' as const,
    background: C.selectBg, border: `1px solid ${C.border}`,
    borderRadius: '7px', color: C.text, padding: '9px 11px', fontSize: '13px',
    outline: 'none', cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s',
  } as React.CSSProperties,
  textarea: {
    width: '100%', boxSizing: 'border-box' as const,
    background: C.inputBg, border: `1px solid ${C.border}`,
    borderRadius: '7px', color: C.text, padding: '9px 11px', fontSize: '12px',
    fontFamily: 'monospace', resize: 'vertical' as const, outline: 'none',
    minHeight: '80px', transition: 'border-color 0.15s, box-shadow 0.15s',
  } as React.CSSProperties,
  row: { display: 'flex', gap: '10px' } as React.CSSProperties,

  sectionDivider: { borderTop: `1px solid ${C.borderFaint}`, margin: '16px 0 14px', paddingTop: '14px' } as React.CSSProperties,
  sectionTitle: { fontSize: '12px', fontWeight: 700, color: C.indigoLight, textTransform: 'uppercase' as const, letterSpacing: '0.6px', marginBottom: '12px' } as React.CSSProperties,
  infoBox: (color: 'blue' | 'amber' | 'red'): React.CSSProperties => ({
    background: color === 'blue' ? 'rgba(99,102,241,0.1)' : color === 'amber' ? 'rgba(251,191,36,0.1)' : 'rgba(239,68,68,0.1)',
    border: `1px solid ${color === 'blue' ? 'rgba(99,102,241,0.25)' : color === 'amber' ? 'rgba(251,191,36,0.25)' : 'rgba(239,68,68,0.25)'}`,
    borderRadius: '7px', padding: '8px 12px', fontSize: '11px',
    color: color === 'blue' ? C.indigoLight : color === 'amber' ? C.amber : C.red,
    marginBottom: '12px',
  }),
  toggleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#fafafa', border: `1px solid ${C.borderFaint}`, borderRadius: '7px', marginBottom: '10px' } as React.CSSProperties,
  toggleLabel: { fontSize: '13px', color: C.text, fontWeight: 500 } as React.CSSProperties,
  toggleSub: { fontSize: '11px', color: C.textFaint, marginTop: '2px' } as React.CSSProperties,
  toggle: (on: boolean): React.CSSProperties => ({
    width: '38px', height: '20px', borderRadius: '10px', flexShrink: 0,
    background: on ? C.indigo : '#d0d0d0', position: 'relative' as const,
    cursor: 'pointer', border: 'none', transition: 'background 0.2s',
  }),
  toggleKnob: (on: boolean): React.CSSProperties => ({
    position: 'absolute' as const, top: '2px', left: on ? '20px' : '2px',
    width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
  }),

  btn: (variant: 'primary' | 'secondary' | 'danger' | 'ghost', sm?: boolean): React.CSSProperties => ({
    padding: sm ? '6px 14px' : '9px 20px',
    borderRadius: '7px', fontSize: sm ? '12px' : '13px', fontWeight: 700,
    cursor: 'pointer',
    background: variant === 'primary' ? C.indigo
      : variant === 'danger' ? 'rgba(220,53,69,0.08)'
      : variant === 'secondary' ? '#f0f0f0'
      : 'transparent',
    color: variant === 'primary' ? '#000' : variant === 'danger' ? C.red : C.text,
    border: variant === 'secondary' ? '1px solid #ddd' : variant === 'danger' ? '1px solid rgba(220,53,69,0.3)' : 'none',
    transition: 'opacity 0.15s',
  }),
  navRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', gap: '8px', paddingTop: '14px', borderTop: `1px solid ${C.borderFaint}` } as React.CSSProperties,

  card: {
    background: C.cardBg, border: `1px solid ${C.border}`,
    borderRadius: '10px', padding: '14px', marginBottom: '10px',
  } as React.CSSProperties,
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' } as React.CSSProperties,
  pill: (color: 'green' | 'grey' | 'indigo' | 'amber'): React.CSSProperties => ({
    fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px',
    background:
      color === 'green'  ? 'rgba(40,167,69,0.12)' :
      color === 'indigo' ? 'rgba(255,215,0,0.2)'  :
      color === 'amber'  ? 'rgba(253,126,20,0.14)' :
      '#eee',
    color:
      color === 'green'  ? C.green :
      color === 'indigo' ? C.indigoLight :
      color === 'amber'  ? C.amber :
      C.textMuted,
  }),
  infoRow: { fontSize: '12px', color: C.textMuted, marginBottom: '5px', lineHeight: '1.4' } as React.CSSProperties,

  mapContainer: {
    width: '100%', height: '260px', borderRadius: '8px',
    border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: '12px',
  } as React.CSSProperties,
  searchWrap: { position: 'relative' as const, marginBottom: '10px' },
  searchResults: {
    position: 'absolute' as const, top: '100%', left: 0, right: 0, zIndex: 20,
    background: '#fff', border: `1px solid ${C.border}`,
    borderRadius: '7px', maxHeight: '180px', overflowY: 'auto' as const, marginTop: '3px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
  } as React.CSSProperties,
  searchResult: {
    padding: '9px 13px', fontSize: '12px', color: C.text, cursor: 'pointer',
    borderBottom: `1px solid ${C.borderFaint}`,
  } as React.CSSProperties,

  reviewRow: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.borderFaint}`, fontSize: '13px' } as React.CSSProperties,
};

// ─── Toggle switch component ──────────────────────────────────────────────────
const Toggle: React.FC<{ on: boolean; onChange: (v: boolean) => void; label: string; sub?: string }> = ({ on, onChange, label, sub }) => (
  <div style={S.toggleRow} onClick={() => onChange(!on)}>
    <div>
      <div style={S.toggleLabel}>{label}</div>
      {sub && <div style={S.toggleSub}>{sub}</div>}
    </div>
    <div style={S.toggle(on)}>
      <div style={S.toggleKnob(on)} />
    </div>
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────
export const ExecutionRuleManager: React.FC<{ walletAddress: string }> = ({ walletAddress }) => {
  const [rules, setRules] = useState<ExecutionRule[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  const [editingRule, setEditingRule] = useState<ExecutionRule | null>(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<RuleForm>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [horizontal, setHorizontal] = useState(window.innerWidth >= 640);

  // Register-contract inline flow
  const [showRegister, setShowRegister] = useState(false);
  const [registerForm, setRegisterForm] = useState<RegisterContractForm>({ address: '', name: '', network: 'testnet' });
  const [registerStep, setRegisterStep] = useState<'idle' | 'discovering' | 'saving' | 'done' | 'error'>('idle');
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [discoveredFunctions, setDiscoveredFunctions] = useState<any[]>([]);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);

  const [locationSearch, setLocationSearch] = useState('');
  const [locationResults, setLocationResults] = useState<any[]>([]);
  const [showResults, setShowResults] = useState(false);

  // ── Country geofence picker ──
  type CountryEntry = { name: string; iso2: string; iso3: string; featureIdx: number };
  const [countryIndex, setCountryIndex] = useState<CountryEntry[] | null>(null);
  const [countryIndexLoading, setCountryIndexLoading] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryResults, setShowCountryResults] = useState(false);
  const countriesGeoRef = useRef<any>(null);
  const countrySearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const MAPBOX_TOKEN = (mapboxgl as any).accessToken || process.env.REACT_APP_MAPBOX_TOKEN || '';
  // Browser traffic goes through the GeoTrust backend proxy, which injects the
  // server-side GeoLink key. The key must never reach the bundle.
  const apiUrl =
    typeof window !== 'undefined'
      ? '/api/v2/geolink/proxy'
      : (process.env.REACT_APP_GEOLINK_API_URL || 'https://testnet.stellargeolink.com');

  // ── Responsive ──
  useEffect(() => {
    const fn = () => setHorizontal(window.innerWidth >= 640);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  /**
   * Authenticated fetch against GeoLink with multi-tenant scoping plumbed in.
   *
   * Contract per docs/geolink-multi-tenant-scoping-response.md §3.2:
   *   - On every request we send `X-Public-Key: <connected_wallet>` so GeoLink
   *     can resolve the caller via header (priority: JWT → header → body).
   *     The backend proxy forwards this header untouched.
   *   - Callers passing a list path append `?public_key=` themselves — this
   *     helper does not do query-string mutation.
   *   - 403 { code: "not_owner" } is surfaced via `setError` so the panel
   *     shows a single-line toast above the wizard.
   */
  const apiFetch = useCallback(async (path: string, opts: RequestInit = {}) => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(opts.headers as Record<string, string> | undefined || {}),
    };
    if (walletAddress) headers['X-Public-Key'] = walletAddress;
    const r = await fetch(`${apiUrl}${path}`, { ...opts, headers });
    const data = await r.json().catch(() => ({}));
    if (r.status === 403 && data?.code === 'not_owner') {
      setError(
        data.details
          ? `This row belongs to another wallet — ${data.details}`
          : 'This row belongs to another wallet.'
      );
    }
    return data;
  }, [apiUrl, walletAddress]);

  // List calls always pin the result set to the connected wallet via
  // `?public_key=` per §3.1 of the scoping guide. Without a wallet we
  // fall back to the server's default behaviour (no scoping).
  //
  // For `/api/contracts` we send `is_active=all` (canonical name from
  // GeoLink commit `944ef6f` on 2026-05-08) so soft-deleted parent
  // contracts come back too. For `/api/contracts/rules` we
  // explicitly fetch both `is_active=true` and `is_active=false` and
  // merge — see `loadRules` for why the alias dance isn't safe on
  // the rules endpoint.
  const buildListUrl = useCallback((basePath: string, extra: Record<string, string> = {}) => {
    const params = new URLSearchParams(extra);
    if (walletAddress) params.set('public_key', walletAddress);
    if (!params.has('is_active')) params.set('is_active', 'all');
    return `${basePath}?${params.toString()}`;
  }, [walletAddress]);

  // Same as `buildListUrl` but lets the caller pin a specific
  // `is_active` value (true / false). Used by `loadRules` to merge
  // both rule states explicitly.
  const buildScopedRulesUrl = useCallback((isActive: 'true' | 'false') => {
    const params = new URLSearchParams();
    if (walletAddress) params.set('public_key', walletAddress);
    params.set('is_active', isActive);
    return `/api/contracts/rules?${params.toString()}`;
  }, [walletAddress]);

  const loadContracts = useCallback(async () => {
    try {
      const d = await apiFetch(buildListUrl('/api/contracts'));
      if (d.success && d.contracts) setContracts(d.contracts);
    } catch { /* ignore */ }
  }, [apiFetch, buildListUrl]);

  const loadRules = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // Why two queries instead of `?is_active=all`: GeoLink's
      // 944ef6f fix only covered `/api/contracts`, not the rules
      // endpoint. We've observed `/api/contracts/rules?is_active=all`
      // silently returning 0 rows after a contract soft-delete →
      // reactivate cycle (the rules' own `is_active` appears to
      // cascade to `false` on contract delete and doesn't reset on
      // contract reactivate, so a default-active filter — which is
      // what GeoLink falls back to when it doesn't recognise `all` —
      // hides them entirely). Fetching both states explicitly is
      // deterministic across any GeoLink version. Network cost is
      // two parallel calls instead of one; payload is tiny.
      const [activeResp, inactiveResp] = await Promise.all([
        apiFetch(buildScopedRulesUrl('true')),
        apiFetch(buildScopedRulesUrl('false')).catch(() => ({ success: false })),
      ]);

      const extract = (resp: any): any[] => {
        if (!resp) return [];
        if (resp.success && Array.isArray(resp.rules)) return resp.rules;
        if (Array.isArray(resp.execution_rules)) return resp.execution_rules;
        return [];
      };
      const merged = new Map<number, any>();
      for (const r of extract(activeResp)) {
        if (typeof r.id === 'number') merged.set(r.id, r);
      }
      for (const r of extract(inactiveResp)) {
        if (typeof r.id === 'number' && !merged.has(r.id)) merged.set(r.id, r);
      }
      const allRules = Array.from(merged.values());
      setRules(allRules);

      if (allRules.length === 0) {
        // Diagnostic: surface raw responses to the console when the
        // merge produces nothing, so we can see whether the rules are
        // genuinely missing or filtered out by some unrelated server
        // condition (auth scope, hard-delete, missing
        // `created_by_public_key`, etc.).
      }

      if (!activeResp?.success && !inactiveResp?.success) {
        setError(activeResp?.error || inactiveResp?.error || 'Failed to load rules');
      }
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [apiFetch, buildScopedRulesUrl]);

  // Initial mount + cross-tab refresh.
  //
  // `RegisteredContractsList` lives in a sibling tab in
  // `SmartContractsOverlay` and dispatches `geotrust:contracts-changed`
  // on any state-affecting mutation (delete, reactivate, migrate). The
  // overlay conditionally mounts each tab so switching to the rules
  // tab usually triggers a fresh `loadContracts/loadRules` via mount
  // anyway — but if the user kept this tab focused while another
  // wallet client (or another tab) flipped a contract's `is_active`,
  // the event is what surfaces the change.
  useEffect(() => { loadContracts(); loadRules(); }, [loadContracts, loadRules]);
  useEffect(() => {
    const onChanged = () => { loadContracts(); loadRules(); };
    window.addEventListener('geotrust:contracts-changed', onChanged as EventListener);
    return () => {
      window.removeEventListener('geotrust:contracts-changed', onChanged as EventListener);
    };
  }, [loadContracts, loadRules]);

  /**
   * Modal hygiene for the wizard overlay:
   *   - Lock body scroll so the underlying rules list can't scroll
   *     behind the dialog and hijack the wheel.
   *   - Listen for Esc on document so the user can dismiss without
   *     having focus inside the dialog.
   * Both are cleanly torn down when the wizard closes.
   */
  useEffect(() => {
    if (!showWizard) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeWizard(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWizard]);

  // ── Register a contract (discover → save) ──
  const handleRegisterContract = async () => {
    const { address, name, network } = registerForm;
    if (!address.trim()) { setRegisterError('Contract address is required'); return; }
    if (!name.trim())    { setRegisterError('Contract name is required'); return; }

    setRegisterError(null);
    setDiscoveredFunctions([]);

    // Step 1: Discover functions
    setRegisterStep('discovering');
    let functions: any[] = [];
    try {
      const d = await apiFetch('/api/contracts/discover', {
        method: 'POST',
        body: JSON.stringify({ contract_address: address.trim(), network }),
      });
      if (!d.success && !d.functions) {
        throw new Error(d.error || d.message || 'Discovery failed');
      }
      functions = d.functions || [];
      setDiscoveredFunctions(functions);
    } catch (e: any) {
      setRegisterStep('error');
      setRegisterError(`Discover failed: ${e.message}`);
      return;
    }

    // Step 2: Register the contract
    setRegisterStep('saving');
    try {
      const registerBody: Record<string, any> = {
        contract_address: address.trim(),
        contract_name: name.trim(),
        network,
        discovered_functions: functions,
        is_active: true,
      };
      // Belt-and-suspenders: GeoLink resolves caller via JWT → X-Public-Key →
      // body (per §3.2). The header is already on every request via apiFetch,
      // but we also stamp the body so GeoLink's `created_by_public_key` is
      // populated even if a future intermediate strips the header.
      if (walletAddress) registerBody.created_by_public_key = walletAddress;
      const d = await apiFetch('/api/contracts', {
        method: 'POST',
        body: JSON.stringify(registerBody),
      });
      if (!d.success && !d.contract) {
        throw new Error(d.error || d.message || 'Registration failed');
      }
      setRegisterStep('done');
      await loadContracts(); // refresh dropdown
      // Auto-select the newly registered contract in the wizard
      const newId = d.contract?.id;
      if (newId) sf({ contract_id: String(newId), function_name: '' });
      setTimeout(() => { setShowRegister(false); setRegisterStep('idle'); setRegisterForm({ address: '', name: '', network: 'testnet' }); setDiscoveredFunctions([]); }, 1200);
    } catch (e: any) {
      setRegisterStep('error');
      setRegisterError(`Register failed: ${e.message}`);
    }
  };

  // ── Map ──
  useEffect(() => {
    if (!showWizard || step !== 1 || !mapContainerRef.current || mapRef.current) return;
    const lat = parseFloat(form.center_latitude) || 34.05;
    const lng = parseFloat(form.center_longitude) || -118.24;
    const m = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [lng, lat], zoom: form.center_latitude ? 12 : 2,
      accessToken: MAPBOX_TOKEN,
    });
    mapRef.current = m;
    m.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Wait for the style to fully load before adding sources/layers or placing markers.
    // Without this guard, editing a rule (coordinates already set) throws
    // "Style is not done loading" because addSource is called synchronously.
    m.once('load', () => {
      if (form.center_latitude && form.center_longitude) {
        placeMarkerAndCircle(m, lng, lat, parseInt(form.radius_meters) || 500);
      }
    });

    m.on('click', (e) => {
      const { lng: lngC, lat: latC } = e.lngLat;
      setForm(prev => ({ ...prev, center_latitude: latC.toFixed(6), center_longitude: lngC.toFixed(6) }));
      placeMarkerAndCircle(m, lngC, latC, parseInt(form.radius_meters) || 500);
    });

    return () => { m.remove(); mapRef.current = null; markerRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWizard, step]);

  // Redraw circle when radius changes — only when style is already loaded.
  useEffect(() => {
    const m = mapRef.current;
    if (!m || step !== 1 || !form.center_latitude || !form.center_longitude) return;
    if (!m.isStyleLoaded()) return;
    updateCircle(m, parseFloat(form.center_longitude), parseFloat(form.center_latitude), parseInt(form.radius_meters) || 500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.radius_meters]);

  const placeMarkerAndCircle = (m: mapboxgl.Map, lng: number, lat: number, r: number) => {
    markerRef.current?.remove();
    const el = document.createElement('div');
    el.style.cssText = 'width:14px;height:14px;background:#FFD700;border-radius:50%;border:2px solid #000;box-shadow:0 0 10px rgba(255,215,0,0.8)';
    markerRef.current = new mapboxgl.Marker({ element: el }).setLngLat([lng, lat]).addTo(m);
    updateCircle(m, lng, lat, r);
  };

  const updateCircle = (m: mapboxgl.Map, lng: number, lat: number, r: number) => {
    // Guard: never call addSource/addLayer before the style is ready.
    if (!m.isStyleLoaded()) return;
    const geo = makeCircleGeoJSON(lng, lat, r);
    if (m.getSource('radius-circle')) {
      (m.getSource('radius-circle') as mapboxgl.GeoJSONSource).setData(geo as any);
    } else {
      m.addSource('radius-circle', { type: 'geojson', data: geo as any });
      m.addLayer({ id: 'radius-circle-fill', type: 'fill', source: 'radius-circle', paint: { 'fill-color': '#FFD700', 'fill-opacity': 0.15 } });
      m.addLayer({ id: 'radius-circle-outline', type: 'line', source: 'radius-circle', paint: { 'line-color': '#996600', 'line-width': 2, 'line-opacity': 0.85 } });
    }
  };

  // ── Location search ──
  const handleLocationSearch = (q: string) => {
    setLocationSearch(q);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (!q.trim()) { setLocationResults([]); setShowResults(false); return; }
    searchDebounce.current = setTimeout(async () => {
      try {
        const r = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${MAPBOX_TOKEN}&limit=5`);
        const d = await r.json();
        setLocationResults(d.features || []); setShowResults(true);
      } catch { /* ignore */ }
    }, 300);
  };

  const handleLocationSelect = (f: any) => {
    const [lng, lat] = f.center;
    setForm(prev => ({ ...prev, center_latitude: lat.toFixed(6), center_longitude: lng.toFixed(6) }));
    setLocationSearch(f.place_name || ''); setShowResults(false);
    const m = mapRef.current;
    if (m) { m.flyTo({ center: [lng, lat], zoom: 13 }); placeMarkerAndCircle(m, lng, lat, parseInt(form.radius_meters) || 500); }
  };

  const handleMyLocation = () => {
    navigator.geolocation?.getCurrentPosition(({ coords }) => {
      const { latitude: lat, longitude: lng } = coords;
      setForm(prev => ({ ...prev, center_latitude: lat.toFixed(6), center_longitude: lng.toFixed(6) }));
      const m = mapRef.current;
      if (m) { m.flyTo({ center: [lng, lat], zoom: 14 }); placeMarkerAndCircle(m, lng, lat, parseInt(form.radius_meters) || 500); }
    });
  };

  // ── Country geofence helpers ──
  const loadCountryIndex = useCallback(async () => {
    if (countryIndex || countryIndexLoading) return;
    setCountryIndexLoading(true);
    try {
      const r = await fetch('/api/countries', { headers: { 'Cache-Control': 'no-cache' } });
      const geo = await r.json();
      countriesGeoRef.current = geo;
      const entries: CountryEntry[] = (geo.features as any[])
        .map((feat: any, idx: number) => {
          const name: string = (feat.properties?.name || '').trim();
          const iso3: string = String(feat.id || '').trim();
          const iso2: string = iso3ToIso2(iso3) || '';
          return { name, iso2, iso3, featureIdx: idx };
        })
        .filter(e => e.name && e.iso3 && e.iso3 !== 'ATA') // skip Antarctica
        .sort((a, b) => a.name.localeCompare(b.name));
      setCountryIndex(entries);
    } catch { /* silently ignore */ }
    finally { setCountryIndexLoading(false); }
  }, [countryIndex, countryIndexLoading]);

  const handleCountrySearch = (q: string) => {
    setCountrySearch(q);
    if (countrySearchRef.current) clearTimeout(countrySearchRef.current);
    setShowCountryResults(true);
  };

  const filteredCountries = countryIndex
    ? (countrySearch.trim()
      ? countryIndex.filter(e =>
          e.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
          e.iso2.toLowerCase().includes(countrySearch.toLowerCase()) ||
          e.iso3.toLowerCase().includes(countrySearch.toLowerCase()))
      : countryIndex)
    : [];

  const handleCountrySelect = (entry: CountryEntry) => {
    const feature = countriesGeoRef.current?.features?.[entry.featureIdx];
    if (!feature?.geometry) return;
    sf({
      geojson_geometry: JSON.stringify(feature.geometry, null, 2),
      selected_country_name: entry.name,
    });
    setCountrySearch(entry.name);
    setShowCountryResults(false);
  };

  // ── Wizard helpers ──
  const openCreate = () => { setEditingRule(null); setForm(DEFAULT_FORM); setStep(0); setShowWizard(true); setError(null); };
  const closeWizard = () => { setShowWizard(false); setEditingRule(null); setForm(DEFAULT_FORM); setStep(0); setError(null); };

  const openEdit = (rule: ExecutionRule) => {
    // Opening the form is where an edit begins, so it is gated here as well
    // as on the button. `refuseUnauthorized` is declared below; that is safe
    // because this only runs from a click, long after render.
    if (refuseUnauthorized(rule, 'edit')) return;
    setEditingRule(rule);
    setForm({
      ...DEFAULT_FORM,
      contract_id: String(rule.contract_id),
      rule_name: rule.rule_name,
      rule_type: rule.rule_type,
      center_latitude: rule.center_latitude != null ? String(rule.center_latitude) : '',
      center_longitude: rule.center_longitude != null ? String(rule.center_longitude) : '',
      radius_meters: rule.radius_meters != null ? String(rule.radius_meters) : '500',
      function_name: rule.function_name,
      function_parameters: rule.function_parameters ? JSON.stringify(rule.function_parameters, null, 2) : '{}',
      trigger_on: (rule.trigger_on as any) || 'enter',
      auto_execute: rule.auto_execute || false,
      requires_confirmation: rule.requires_confirmation !== false,
      submit_readonly_to_ledger: rule.submit_readonly_to_ledger || false,
      auto_deactivate_on_balance_threshold: rule.auto_deactivate_on_balance_threshold || false,
      max_executions_per_public_key: rule.max_executions_per_public_key != null ? String(rule.max_executions_per_public_key) : '',
      execution_time_window_seconds: rule.execution_time_window_seconds != null ? String(rule.execution_time_window_seconds) : '',
      min_location_duration_seconds: rule.min_location_duration_seconds != null ? String(rule.min_location_duration_seconds) : '',
      target_wallet_public_key: rule.target_wallet_public_key ?? '',
      required_wallet_public_keys: Array.isArray(rule.required_wallet_public_keys)
        ? rule.required_wallet_public_keys.join(', ')
        : '',
      minimum_wallet_count: rule.minimum_wallet_count != null ? String(rule.minimum_wallet_count) : '',
      quorum_type: rule.quorum_type ?? 'any',
      balance_threshold_xlm: rule.balance_threshold_xlm != null ? String(rule.balance_threshold_xlm) : '',
      balance_check_asset_address: rule.balance_check_asset_address ?? '',
      use_smart_wallet_balance: rule.use_smart_wallet_balance ?? false,
      geofence_input_mode: rule.geofence_id != null ? 'id' : 'geojson',
      geofence_id: rule.geofence_id != null ? String(rule.geofence_id) : '',
      geojson_geometry: rule.geojson_geometry ? JSON.stringify(rule.geojson_geometry, null, 2) : '',
      selected_country_name: '',
    });
    setStep(0); setShowWizard(true); setError(null);
  };

  const canNext = () => {
    if (step === 0) return !!(form.contract_id && form.rule_name);
    if (step === 1) {
      if (form.rule_type === 'geofence') {
        if (form.geofence_input_mode === 'id') return !!form.geofence_id;
        if (form.geofence_input_mode === 'country') return !!form.selected_country_name && !!form.geojson_geometry.trim();
        return !!form.geojson_geometry.trim();
      }
      return !!(form.center_latitude && form.center_longitude && form.radius_meters);
    }
    if (step === 2) return !!form.function_name;
    return true;
  };

  const selectedContract = contracts.find(c => String(c.id) === form.contract_id) || null;
  const contractFunctions: any[] = selectedContract?.discovered_functions
    ? (Array.isArray(selectedContract.discovered_functions) ? selectedContract.discovered_functions : Object.values(selectedContract.discovered_functions))
    : [];

  /** Find the full function descriptor object for the currently selected function name. */
  const selectedFunctionDef = contractFunctions.find(
    fn => (fn.name || fn) === form.function_name,
  ) || null;

  /** GeoLink auto-detects readonly functions by name prefix and simulates them — no signing needed. */
  const READ_ONLY_PREFIXES = ['get_', 'is_', 'has_', 'check_', 'query_', 'view_', 'read_', 'fetch_'];
  const isReadOnlyFn = !!form.function_name &&
    READ_ONLY_PREFIXES.some(p => form.function_name.toLowerCase().startsWith(p));

  // ── Simulate state (readonly function test) ──────────────────────────────
  const [simulating, setSimulating]       = useState(false);
  const [simResult, setSimResult]         = useState<SimulateResult | null>(null);
  const [simError, setSimError]           = useState<string | null>(null);

  const handleSimulate = useCallback(async () => {
    if (!form.function_name || !form.contract_id) return;
    setSimulating(true);
    setSimResult(null);
    setSimError(null);
    try {
      let params: Record<string, any> = {};
      if (form.function_parameters) {
        try { params = JSON.parse(form.function_parameters); } catch (_) { params = {}; }
      }
      const result = await simulateContractFunction(
        form.contract_id,
        form.function_name,
        params,
        walletAddress,
      );
      setSimResult(result);
    } catch (err: any) {
      setSimError(err.message || 'Simulation failed');
    } finally {
      setSimulating(false);
    }
  }, [form.contract_id, form.function_name, form.function_parameters, walletAddress]);

  /** Build a JSON template object from a function's parameter list. */
  const buildParamTemplate = (fnDef: any): Record<string, unknown> => {
    const params: any[] = fnDef?.parameters || fnDef?.params || [];
    const template: Record<string, unknown> = {};
    for (const p of params) {
      const name = p.name ?? p;
      const type = (p.type ?? p.param_type ?? '').toString().toLowerCase();
      if (type.includes('address')) template[name] = 'G...';
      else if (type.includes('bool'))  template[name] = false;
      else if (type.includes('u32') || type.includes('i32') || type.includes('u64') || type.includes('i64')) template[name] = 0;
      else if (type.includes('u128') || type.includes('i128')) template[name] = '0';
      else if (type.includes('vec'))   template[name] = [];
      else if (type.includes('map'))   template[name] = {};
      else if (type.includes('bytes')) template[name] = '';
      else if (type.includes('string')) template[name] = '';
      else template[name] = null;
    }
    return template;
  };

  /** Type badge colour for the parameter reference panel. */
  const typeColor = (type: string): string => {
    const t = (type || '').toLowerCase();
    if (t.includes('address')) return '#60a5fa';
    if (t.includes('bool'))    return '#a78bfa';
    if (t.includes('vec'))     return '#34d399';
    if (t.includes('map'))     return '#fbbf24';
    if (t.includes('u32') || t.includes('u64') || t.includes('u128') ||
        t.includes('i32') || t.includes('i64') || t.includes('i128')) return '#f97316';
    if (t.includes('bytes'))   return '#f472b6';
    return C.textMuted;
  };

  const f = form; const sf = (patch: Partial<RuleForm>) => setForm(prev => ({ ...prev, ...patch }));

  /** Handle function selection: update name AND auto-scaffold parameters if textarea is empty / default. */
  const handleFunctionSelect = (fnName: string) => {
    const fnDef = contractFunctions.find(fn => (fn.name || fn) === fnName);
    const currentParams = f.function_parameters.trim();
    const isDefault = !currentParams || currentParams === '{}' || currentParams === '{\n\n}';
    if (fnDef && isDefault) {
      const template = buildParamTemplate(fnDef);
      sf({ function_name: fnName, function_parameters: JSON.stringify(template, null, 2) });
    } else {
      sf({ function_name: fnName });
    }
  };

  // ── Save ──
  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      // ── Client-side validation ──
      if (!f.rule_name.trim()) { setError('Rule name is required'); setSaving(false); return; }
      if (!f.contract_id)      { setError('Please select a contract'); setSaving(false); return; }
      if (f.rule_type === 'location' || f.rule_type === 'proximity') {
        if (!f.center_latitude || !f.center_longitude || !f.radius_meters) {
          setError('Please provide center coordinates and radius'); setSaving(false); return;
        }
      }
      if (f.rule_type === 'geofence') {
        if (f.geofence_input_mode === 'id' && !f.geofence_id) {
          setError('Please provide a geofence ID or paste a GeoJSON geometry'); setSaving(false); return;
        }
        if (f.geofence_input_mode === 'country' && !f.selected_country_name) {
          setError('Please select a country'); setSaving(false); return;
        }
        if (f.geofence_input_mode === 'geojson' || f.geofence_input_mode === 'country') {
          if (!f.geojson_geometry.trim()) {
            setError('Please provide a geofence ID or paste a GeoJSON geometry'); setSaving(false); return;
          }
          try {
            const geo = JSON.parse(f.geojson_geometry);
            if (geo.type !== 'Polygon' && geo.type !== 'MultiPolygon') {
              setError('GeoJSON type must be "Polygon" or "MultiPolygon" — paste only the geometry object, not the whole Feature'); setSaving(false); return;
            }
            if (!geo.coordinates || !Array.isArray(geo.coordinates) || geo.coordinates.length === 0) {
              setError('GeoJSON geometry has no coordinates'); setSaving(false); return;
            }
          } catch {
            setError('Invalid JSON — please check your GeoJSON geometry'); setSaving(false); return;
          }
        }
      }
      if (f.trigger_on === 'dwell' && (!f.min_location_duration_seconds || parseInt(f.min_location_duration_seconds) <= 0)) {
        setError('Please set a minimum dwell time in seconds'); setSaving(false); return;
      }
      if (f.max_executions_per_public_key && !f.execution_time_window_seconds) {
        setError('Please set a time window when limiting executions'); setSaving(false); return;
      }
      if (f.execution_time_window_seconds && !f.max_executions_per_public_key) {
        setError('Please set max executions when specifying a time window'); setSaving(false); return;
      }
      if (f.required_wallet_public_keys) {
        const wallets = f.required_wallet_public_keys.split(',').map(s => s.trim()).filter(Boolean);
        if (!f.minimum_wallet_count) {
          setError('Please set the minimum wallet count for quorum'); setSaving(false); return;
        }
        const minCount = parseInt(f.minimum_wallet_count);
        if (minCount <= 0) {
          setError('Minimum wallet count must be at least 1'); setSaving(false); return;
        }
        if (minCount > wallets.length) {
          setError('Minimum count cannot exceed the number of wallets listed'); setSaving(false); return;
        }
      }

      let params: Record<string, any> = {};
      try { params = JSON.parse(f.function_parameters || '{}'); } catch { params = {}; }

      // Fields sent for both create and update
      const common: Record<string, any> = {
        rule_name:              f.rule_name,
        rule_type:              f.rule_type,
        function_name:          f.function_name,
        function_parameters:    params,
        trigger_on:             f.trigger_on,
        auto_execute:           f.auto_execute,
        requires_confirmation:  f.requires_confirmation,
        submit_readonly_to_ledger: f.submit_readonly_to_ledger,
        auto_deactivate_on_balance_threshold: f.auto_deactivate_on_balance_threshold,
      };

      // Geospatial — location / proximity rules
      if (f.rule_type === 'location' || f.rule_type === 'proximity') {
        if (f.center_latitude && f.center_longitude) {
          common.center_latitude  = parseFloat(f.center_latitude);
          common.center_longitude = parseFloat(f.center_longitude);
          common.radius_meters    = parseInt(f.radius_meters || '500');
        }
      }
      // Geospatial — geofence rules
      if (f.rule_type === 'geofence') {
        if (f.geofence_input_mode === 'id' && f.geofence_id) {
          common.geofence_id = parseInt(f.geofence_id);
        }
        if ((f.geofence_input_mode === 'geojson' || f.geofence_input_mode === 'country') && f.geojson_geometry.trim()) {
          try { common.geojson_geometry = JSON.parse(f.geojson_geometry); } catch { /* already validated above */ }
        }
      }

      // Optional fields — only include when actually set
      if (f.target_wallet_public_key)  common.target_wallet_public_key   = f.target_wallet_public_key;
      if (f.required_wallet_public_keys) common.required_wallet_public_keys = f.required_wallet_public_keys.split(',').map((s: string) => s.trim()).filter(Boolean);
      if (f.minimum_wallet_count)      common.minimum_wallet_count        = parseInt(f.minimum_wallet_count);
      if (f.quorum_type)               common.quorum_type                 = f.quorum_type;
      if (f.max_executions_per_public_key) common.max_executions_per_public_key = parseInt(f.max_executions_per_public_key);
      if (f.execution_time_window_seconds) common.execution_time_window_seconds = parseInt(f.execution_time_window_seconds);
      // Dwell time — only meaningful when trigger_on = "dwell"
      if (f.trigger_on === 'dwell' && f.min_location_duration_seconds) common.min_location_duration_seconds = parseInt(f.min_location_duration_seconds);
      // Balance auto-deactivation
      if (f.auto_deactivate_on_balance_threshold && f.balance_threshold_xlm) common.balance_threshold_xlm = parseFloat(f.balance_threshold_xlm);
      if (f.balance_check_asset_address) common.balance_check_asset_address = f.balance_check_asset_address;
      if (f.use_smart_wallet_balance)  common.use_smart_wallet_balance    = true;

      let data: any;
      if (editingRule) {
        // PUT — partial update; do NOT send contract_id (immutable) or public_key (not a rule field).
        // X-Public-Key on every request lets GeoLink enforce the
        // `403 { code: "not_owner" }` ownership gate per §3.2.
        data = await apiFetch(`/api/contracts/rules/${editingRule.id}`, { method: 'PUT', body: JSON.stringify(common) });
      } else {
        // POST — create requires contract_id. Stamp `created_by_public_key`
        // in the body too (header → body fallback chain per §3.2).
        const createBody: Record<string, any> = { ...common, contract_id: parseInt(f.contract_id) };
        if (walletAddress) createBody.created_by_public_key = walletAddress;
        data = await apiFetch('/api/contracts/rules', { method: 'POST', body: JSON.stringify(createBody) });
      }

      if (data.success) {
        closeWizard(); await loadRules();
      } else {
        // Surface the full GeoLink error (may include detail beyond the top-level message)
        const detail = data.details || data.message || data.error || JSON.stringify(data);
        setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      }
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  /**
   * Authority over each rule, resolved through the spatial object that links
   * to it.
   *
   * Creator ownership is no longer the decision for a rule an object points
   * at — `can_manage_rules` on that object is, and it can say yes to an
   * organization operator who did not author the rule and no to the wallet
   * that did. `isOwnRule` below survives only as the fallback inside
   * `getRuleAuthorization`, for rules nothing points at.
   *
   * This governs *managing* rules. Executing one is a separate act with its
   * own checks, and nothing here is consulted on that path.
   */
  const [ruleAuthority, setRuleAuthority] = useState<Map<string, RuleAuthorization>>(new Map());
  const [resolvingAuthority, setResolvingAuthority] = useState(false);
  // Read by the mutation handlers, which are recreated per render but may
  // still be invoked from a closure created before the latest answer landed.
  const ruleAuthorityRef = useRef<Map<string, RuleAuthorization>>(ruleAuthority);
  useEffect(() => { ruleAuthorityRef.current = ruleAuthority; }, [ruleAuthority]);

  useEffect(() => {
    if (!walletAddress || rules.length === 0) {
      setRuleAuthority(new Map());
      return;
    }
    let cancelled = false;
    setResolvingAuthority(true);
    getRuleAuthorizations(rules, walletAddress)
      .then((map) => { if (!cancelled) setRuleAuthority(map); })
      // A failure leaves the map empty, which denies every rule. Rules are
      // automation attached to physical places; the wrong way to fail is to
      // leave them all editable.
      .catch(() => { if (!cancelled) setRuleAuthority(new Map()); })
      .finally(() => { if (!cancelled) setResolvingAuthority(false); });
    return () => { cancelled = true; };
  }, [rules, walletAddress]);

  /**
   * Refuse a mutation the wallet is not authorized for, and say why.
   *
   * Checked in the handler and not only on the button, because a disabled
   * button is a hint. GeoLink also enforces its own creator scoping, but
   * that is a different rule from the one the chain states, and a UI that
   * relies on someone else's refusal cannot explain its own.
   */
  const refuseUnauthorized = (rule: ExecutionRule, verb: string): boolean => {
    const authority = ruleAuthorityRef.current.get(String(rule.id)) ?? null;
    if (authority?.canManageRule) return false;
    setError(
      authority?.reason
        ? `Cannot ${verb} this rule: ${authority.reason}`
        : `Cannot ${verb} this rule: authority over it has not been resolved yet.`,
    );
    return true;
  };

  const handleToggle = async (rule: ExecutionRule) => {
    if (refuseUnauthorized(rule, rule.is_active ? 'deactivate' : 'activate')) return;
    try { const d = await apiFetch(`/api/contracts/rules/${rule.id}`, { method: 'PUT', body: JSON.stringify({ is_active: !rule.is_active }) }); if (d.success) loadRules(); } catch { /* ignore */ }
  };
  const handleDelete = async (rule: ExecutionRule) => {
    if (refuseUnauthorized(rule, 'delete')) return;
    if (!window.confirm(`Delete rule "${rule.rule_name}"?`)) return;
    try { const d = await apiFetch(`/api/contracts/rules/${rule.id}`, { method: 'DELETE' }); if (d.success) loadRules(); else setError(d.error || 'Delete failed'); } catch (e: any) { setError(e.message); }
  };
  const authorityFor = (rule: ExecutionRule): RuleAuthorization | null =>
    ruleAuthority.get(String(rule.id)) ?? null;

  /** Whether the management controls should be offered for this rule. */
  const mayManageRule = (rule: ExecutionRule): boolean =>
    authorityFor(rule)?.canManageRule === true;

  /**
   * Mirror predicate for contracts. Phase 1 keeps non-owned contracts visible
   * (they're picked from a dropdown when authoring a rule), but in any
   * mutation UI we'll gate the write buttons on this. Returns `false` when
   * the wallet isn't connected so we never grant ownership by default.
   */
  const isOwnContract = (c: Contract): boolean => {
    if (!walletAddress) return false;
    if (c.created_by_public_key) return c.created_by_public_key === walletAddress;
    if (c.owner_public_key)      return c.owner_public_key === walletAddress;
    return false;
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={S.wrap}>
      {/* Scoped styles — fixes placeholder colour, select arrow and autofill on Windows/Chrome */}
      <style>{`
        .erm-input::placeholder { color: #aaa; opacity: 1; }
        .erm-input:focus        { border-color: #FFD700 !important; box-shadow: 0 0 0 2px rgba(255,215,0,0.25); }
        .erm-select             { appearance: none; -webkit-appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23888' d='M6 8L1 3h10z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 10px center; padding-right: 28px !important; }
        .erm-select:focus       { border-color: #FFD700 !important; outline: none; box-shadow: 0 0 0 2px rgba(255,215,0,0.25); }
        .erm-input:-webkit-autofill,
        .erm-input:-webkit-autofill:focus { -webkit-box-shadow: 0 0 0 1000px #fff inset !important; -webkit-text-fill-color: #333 !important; }
        .erm-textarea::placeholder { color: #aaa; opacity: 1; }
        .erm-textarea:focus     { border-color: #FFD700 !important; outline: none; box-shadow: 0 0 0 2px rgba(255,215,0,0.25); }
      `}</style>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <div style={{ fontSize: '14px', fontWeight: 700, color: C.text }}>Execution Rules</div>
          <div style={{ fontSize: '11px', color: C.textFaint }}>Geo-triggered smart contract automation</div>
        </div>
        <button style={S.btn('primary', true)} onClick={openCreate}>+ New Rule</button>
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '7px', padding: '9px 13px', fontSize: '12px', color: C.red, marginBottom: '12px' }}>
          ⚠ {error}
        </div>
      )}

      {/*
        ── Wizard (Create / Edit) ──
        Rendered as a full-screen modal overlay so the rule list isn't
        pushed off-screen when editing. The backdrop click + Esc both
        dismiss; clicks inside the panel stop propagation. The inner
        panel handles its own vertical overflow so long forms stay
        scrollable on small viewports without losing the sticky header
        / footer feel.
      */}
      {showWizard && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={editingRule ? `Edit rule: ${editingRule.rule_name}` : 'Create execution rule'}
          onClick={closeWizard}
          onKeyDown={(e) => { if (e.key === 'Escape') closeWizard(); }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '4vh 16px',
            overflowY: 'auto',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.wizardBg,
              border: `1px solid ${C.border}`,
              borderRadius: '12px',
              padding: '18px',
              boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
              width: '100%',
              maxWidth: '720px',
              maxHeight: '92vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', flexShrink: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: C.text }}>
              {editingRule ? `Edit: ${editingRule.rule_name}` : 'Create Execution Rule'}
            </div>
            <button style={S.btn('ghost', true)} onClick={closeWizard} aria-label="Close">✕</button>
          </div>

          {/*
            Body scroll container. Stepper + step content live here so
            the header (title + close button) and footer (nav buttons)
            remain visible while the form scrolls on tall steps.
          */}
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, paddingRight: 4 }}>

          {/* Stepper */}
          <div style={S.stepperWrap(horizontal)}>
            {STEPS.map((label, i) => (
              <React.Fragment key={i}>
                <div style={S.stepItem(horizontal)}>
                  <div style={S.stepDot(i === step, i < step)}>{i < step ? '✓' : i + 1}</div>
                  <div style={S.stepLabel(i === step, i < step)}>{label}</div>
                </div>
                {i < STEPS.length - 1 && <div style={S.connector(horizontal)} />}
              </React.Fragment>
            ))}
          </div>

          {/* ── Step 0: Details ── */}
          {step === 0 && (
            <div>
              <div style={S.field}>
                <label style={S.label}>Rule Name *</label>
                <input className="erm-input" style={S.input} value={f.rule_name} onChange={e => sf({ rule_name: e.target.value })} placeholder="e.g. Payment at Coffee Shop" />
              </div>
              <div style={S.field}>
                <label style={S.label}>Contract *</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <select className="erm-select" style={{ ...S.select, flex: 1 }} value={f.contract_id} onChange={e => sf({ contract_id: e.target.value, function_name: '' })}>
                    <option value="">Select a contract…</option>
                    {contracts.map(c => {
                      // Multi-tenant cue per docs/geolink-multi-tenant-scoping-response.md §5:
                      // shared contracts (registered by another wallet) get a
                      // "(shared)" suffix so the author knows they're picking
                      // someone else's contract registration. Soroban contract
                      // addresses are global — pointing a rule at a shared
                      // contract is allowed; mutating its registration is not.
                      const owned = isOwnContract(c);
                      return (
                        <option key={c.id} value={c.id}>
                          {c.contract_name} ({c.contract_address.slice(0, 8)}…){owned ? '' : ' · shared'}
                        </option>
                      );
                    })}
                  </select>
                  <button
                    style={{ ...S.btn('secondary', true), whiteSpace: 'nowrap', fontSize: '11px', flexShrink: 0 }}
                    onClick={() => { setShowRegister(v => !v); setRegisterStep('idle'); setRegisterError(null); }}
                    title="Register a new contract so it appears in this dropdown"
                  >
                    {showRegister ? '✕ Cancel' : '+ Register'}
                  </button>
                </div>
                {contracts.length === 0 && !showRegister && (
                  <div style={{ fontSize: '11px', color: C.amber, marginTop: '6px' }}>
                    No contracts registered yet. Click <strong>+ Register</strong> to add one.
                  </div>
                )}
              </div>

              {/* ── Inline register contract panel ── */}
              {showRegister && (
                <div style={{ background: '#0a1220', border: `1px solid ${C.indigo}33`, borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: C.indigoLight, marginBottom: '12px' }}>
                    Register Contract
                  </div>

                  {/* What to register — helper */}
                  <div style={{ ...S.infoBox('blue'), fontSize: '11px', lineHeight: '1.6' }}>
                    <strong>Which contract?</strong><br />
                    • To trigger a deposit into GeoTrust → use your <strong>GeoVault address</strong><br />
                    • To deposit into a DeFindex vault → use the <strong>DeFindex vault address</strong> (not the factory)<br />
                    • To geo-trigger DeFindex vault creation → use the <strong>factory address</strong>
                  </div>

                  <div style={S.field}>
                    <label style={S.label}>Contract Address *</label>
                    <input
                      className="erm-input"
                      style={S.input}
                      value={registerForm.address}
                      onChange={e => setRegisterForm(p => ({ ...p, address: e.target.value.trim() }))}
                      placeholder="C... (Soroban contract address)"
                      disabled={registerStep === 'discovering' || registerStep === 'saving'}
                    />
                  </div>

                  <div style={{ ...S.row, gap: '10px' }}>
                    <div style={{ ...S.field, flex: 2 }}>
                      <label style={S.label}>Display Name *</label>
                      <input
                        className="erm-input"
                        style={S.input}
                        value={registerForm.name}
                        onChange={e => setRegisterForm(p => ({ ...p, name: e.target.value }))}
                        placeholder="e.g. GeoVault US, DeFindex USDC Vault"
                        disabled={registerStep === 'discovering' || registerStep === 'saving'}
                      />
                    </div>
                    <div style={{ ...S.field, flex: 1 }}>
                      <label style={S.label}>Network</label>
                      <select
                        className="erm-select"
                        style={S.select}
                        value={registerForm.network}
                        onChange={e => setRegisterForm(p => ({ ...p, network: e.target.value as any }))}
                        disabled={registerStep === 'discovering' || registerStep === 'saving'}
                      >
                        <option value="testnet">Testnet</option>
                        <option value="mainnet">Mainnet</option>
                      </select>
                    </div>
                  </div>

                  {/* Discovered functions preview */}
                  {discoveredFunctions.length > 0 && (
                    <div style={{ background: 'rgba(74,222,128,0.07)', border: `1px solid rgba(74,222,128,0.2)`, borderRadius: '7px', padding: '10px 12px', marginBottom: '12px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: C.green, marginBottom: '6px' }}>
                        ✓ {discoveredFunctions.length} function{discoveredFunctions.length !== 1 ? 's' : ''} discovered
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                        {discoveredFunctions.slice(0, 12).map((fn: any, i) => (
                          <span key={i} style={{ fontSize: '10px', background: 'rgba(99,102,241,0.15)', color: C.indigoLight, padding: '2px 7px', borderRadius: '4px', fontFamily: 'monospace' }}>
                            {fn.name || fn}
                          </span>
                        ))}
                        {discoveredFunctions.length > 12 && (
                          <span style={{ fontSize: '10px', color: C.textFaint }}>+{discoveredFunctions.length - 12} more</span>
                        )}
                      </div>
                    </div>
                  )}

                  {registerError && (
                    <div style={{ fontSize: '11px', color: C.red, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '6px', padding: '8px 10px', marginBottom: '10px' }}>
                      ⚠ {registerError}
                    </div>
                  )}

                  {registerStep === 'done' && (
                    <div style={{ fontSize: '11px', color: C.green, marginBottom: '8px' }}>✓ Contract registered — you can now select it above</div>
                  )}

                  <button
                    style={{
                      ...S.btn('primary', true),
                      opacity: (registerStep === 'discovering' || registerStep === 'saving') ? 0.6 : 1,
                      width: '100%',
                    }}
                    onClick={handleRegisterContract}
                    disabled={registerStep === 'discovering' || registerStep === 'saving' || registerStep === 'done'}
                  >
                    {registerStep === 'discovering' ? '⏳ Discovering functions…'
                      : registerStep === 'saving' ? '⏳ Registering…'
                      : registerStep === 'done' ? '✓ Done'
                      : 'Discover & Register'}
                  </button>
                </div>
              )}
              <div style={S.field}>
                <label style={S.label}>Rule Type *</label>
                <select className="erm-select" style={S.select} value={f.rule_type} onChange={e => sf({ rule_type: e.target.value as any })}>
                  <option value="location">📍 Location — radius around a point</option>
                  <option value="proximity">🔭 Proximity — relative distance between wallets</option>
                  <option value="geofence">🗺 Geofence — custom polygon area</option>
                </select>
              </div>
            </div>
          )}

          {/* ── Step 1: Location ── */}
          {step === 1 && (
            <div>
              {f.rule_type === 'geofence' ? (
                <div>
                  <div style={{ ...S.infoBox('blue'), fontSize: '12px', lineHeight: '1.6' }}>
                    A geofence fires when a wallet enters/exits/dwells within a custom polygon boundary — e.g. a country border or a district.
                  </div>

                  {/* Mode toggle — 3 options */}
                  <div style={{ display: 'flex', gap: '0', marginBottom: '16px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${C.border}` }}>
                    {([
                      ['country', '🌍 Select Country'],
                      ['id',      '🔢 Geofence ID'],
                      ['geojson', '📋 Paste GeoJSON'],
                    ] as const).map(([mode, label]) => (
                      <button
                        key={mode}
                        style={{
                          flex: 1, padding: '9px 4px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                          background: f.geofence_input_mode === mode ? C.indigo : '#f8f9fa',
                          color: f.geofence_input_mode === mode ? '#000' : C.textMuted,
                          border: 'none', borderRight: mode !== 'geojson' ? `1px solid ${C.border}` : 'none',
                          transition: 'background 0.15s', whiteSpace: 'nowrap' as const,
                        }}
                        onClick={() => {
                          sf({ geofence_input_mode: mode });
                          if (mode === 'country') { loadCountryIndex(); setCountrySearch(f.selected_country_name || ''); setShowCountryResults(false); }
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* ── Country picker ── */}
                  {f.geofence_input_mode === 'country' && (
                    <div>
                      {countryIndexLoading && (
                        <div style={{ fontSize: '12px', color: C.textFaint, marginBottom: '10px' }}>⏳ Loading country data…</div>
                      )}
                      {!countryIndexLoading && (
                        <div style={{ position: 'relative' as const, marginBottom: '12px' }}>
                          <input
                            className="erm-input"
                            style={S.input}
                            value={countrySearch}
                            onChange={e => { handleCountrySearch(e.target.value); if (f.selected_country_name) sf({ selected_country_name: '', geojson_geometry: '' }); }}
                            onFocus={() => { if (countryIndex) setShowCountryResults(true); }}
                            onBlur={() => setTimeout(() => setShowCountryResults(false), 150)}
                            placeholder="🔍 Search country name or ISO code…"
                          />
                          {showCountryResults && filteredCountries.length > 0 && (
                            <div style={{
                              position: 'absolute' as const, top: '100%', left: 0, right: 0, zIndex: 30,
                              background: '#fff', border: `1px solid ${C.border}`, borderRadius: '7px',
                              maxHeight: '220px', overflowY: 'auto' as const, marginTop: '3px',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            }}>
                              {filteredCountries.slice(0, 80).map(entry => (
                                <div
                                  key={entry.iso3}
                                  style={{
                                    padding: '8px 12px', fontSize: '12px', cursor: 'pointer',
                                    borderBottom: `1px solid ${C.borderFaint}`,
                                    background: f.selected_country_name === entry.name ? 'rgba(255,215,0,0.12)' : 'transparent',
                                    display: 'flex', alignItems: 'center', gap: '10px',
                                  }}
                                  onMouseDown={() => handleCountrySelect(entry)}
                                >
                                  <span style={{ fontSize: '16px', lineHeight: 1 }}>
                                    {entry.iso2 ? String.fromCodePoint(...entry.iso2.toUpperCase().split('').map(c => 0x1F1E6 + c.charCodeAt(0) - 65)) : '🌐'}
                                  </span>
                                  <span style={{ color: C.text, fontWeight: 500, flex: 1 }}>{entry.name}</span>
                                  <span style={{ fontSize: '10px', color: C.textFaint, fontFamily: 'monospace' }}>{entry.iso2 || entry.iso3}</span>
                                </div>
                              ))}
                              {filteredCountries.length > 80 && (
                                <div style={{ padding: '8px 12px', fontSize: '11px', color: C.textFaint }}>
                                  …type more to narrow results ({filteredCountries.length} total)
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Selected country confirmation */}
                      {f.selected_country_name && f.geojson_geometry && (
                        <div style={{
                          background: 'rgba(40,167,69,0.1)', border: '1px solid rgba(40,167,69,0.3)',
                          borderRadius: '8px', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '10px',
                        }}>
                          <span style={{ fontSize: '20px' }}>
                            {(() => {
                              const entry = countryIndex?.find(e => e.name === f.selected_country_name);
                              if (!entry?.iso2) return '🌍';
                              try { return String.fromCodePoint(...entry.iso2.toUpperCase().split('').map(c => 0x1F1E6 + c.charCodeAt(0) - 65)); } catch { return '🌍'; }
                            })()}
                          </span>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: C.green }}>✓ {f.selected_country_name}</div>
                            <div style={{ fontSize: '11px', color: C.textFaint }}>
                              {(() => { try { const g = JSON.parse(f.geojson_geometry); return `${g.type} geometry loaded · ${formatArea(geometryAreaSqMeters(g))}`; } catch { return 'Geometry loaded'; } })()}
                            </div>
                          </div>
                          <button
                            style={{ ...S.btn('ghost', true), marginLeft: 'auto', fontSize: '11px', color: C.red }}
                            onClick={() => { sf({ selected_country_name: '', geojson_geometry: '' }); setCountrySearch(''); }}
                          >
                            ✕ Clear
                          </button>
                        </div>
                      )}
                      {!f.selected_country_name && !countryIndexLoading && countryIndex && (
                        <div style={S.hint}>
                          {countryIndex.length} countries available. The country border will be used as the geofence polygon.
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Geofence by ID ── */}
                  {f.geofence_input_mode === 'id' && (
                    <div style={S.field}>
                      <label style={S.label}>Geofence ID *</label>
                      <input
                        className="erm-input" style={S.input} type="number" min="1"
                        value={f.geofence_id}
                        onChange={e => sf({ geofence_id: e.target.value })}
                        placeholder="Numeric ID returned by POST /api/geofences"
                      />
                      <div style={S.hint}>Use this when the same polygon is reused across multiple rules.</div>
                    </div>
                  )}

                  {/* ── Paste GeoJSON ── */}
                  {f.geofence_input_mode === 'geojson' && (
                    <div style={S.field}>
                      <label style={S.label}>GeoJSON Geometry *</label>
                      <textarea
                        className="erm-textarea"
                        style={{ ...S.textarea, minHeight: '180px', fontSize: '11px' }}
                        value={f.geojson_geometry}
                        onChange={e => sf({ geojson_geometry: e.target.value })}
                        placeholder={'{\n  "type": "Polygon",\n  "coordinates": [\n    [[-9.5,36.0],[3.3,36.0],[3.3,43.8],[-9.5,43.8],[-9.5,36.0]]\n  ]\n}'}
                        rows={8}
                      />
                      <div style={{ display: 'flex', gap: '12px', marginTop: '5px', flexWrap: 'wrap' as const }}>
                        <div style={S.hint}>Paste the <code>geometry</code> object only — NOT the whole Feature or FeatureCollection.</div>
                        {(() => {
                          if (!f.geojson_geometry.trim()) return null;
                          try {
                            const geo = JSON.parse(f.geojson_geometry);
                            if (geo.type !== 'Polygon' && geo.type !== 'MultiPolygon')
                              return <div style={{ fontSize: '11px', color: C.red }}>⚠ type must be "Polygon" or "MultiPolygon"</div>;
                            if (!geo.coordinates || !Array.isArray(geo.coordinates) || geo.coordinates.length === 0)
                              return <div style={{ fontSize: '11px', color: C.red }}>⚠ coordinates missing or empty</div>;
                            const area = formatArea(geometryAreaSqMeters(geo));
                            return <div style={{ fontSize: '11px', color: C.green }}>✓ valid {geo.type} · {area}</div>;
                          } catch {
                            return <div style={{ fontSize: '11px', color: C.red }}>⚠ invalid JSON</div>;
                          }
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div style={S.searchWrap}>
                    <input className="erm-input" style={S.input} value={locationSearch}
                      onChange={e => handleLocationSearch(e.target.value)}
                      onFocus={() => locationResults.length > 0 && setShowResults(true)}
                      placeholder="🔍 Search for an address or place…" />
                    {showResults && locationResults.length > 0 && (
                      <div style={S.searchResults}>
                        {locationResults.map((feat, i) => (
                          <div key={i} style={S.searchResult} onMouseDown={() => handleLocationSelect(feat)}>
                            {feat.place_name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
                    <button style={{ ...S.btn('secondary', true), fontSize: '11px' }} onClick={handleMyLocation}>📍 My Location</button>
                    {f.center_latitude && (
                      <span style={{ fontSize: '11px', color: C.textFaint }}>
                        📌 {parseFloat(f.center_latitude).toFixed(4)}, {parseFloat(f.center_longitude).toFixed(4)}
                      </span>
                    )}
                  </div>
                  <div style={S.mapContainer} ref={mapContainerRef} />
                  <div style={S.row}>
                    <div style={{ ...S.field, flex: 1 }}>
                      <label style={S.label}>Latitude</label>
                      <input className="erm-input" style={S.input} type="number" step="any" value={f.center_latitude}
                        onChange={e => {
                          sf({ center_latitude: e.target.value });
                          const m = mapRef.current;
                          if (m && e.target.value && f.center_longitude) placeMarkerAndCircle(m, parseFloat(f.center_longitude), parseFloat(e.target.value), parseInt(f.radius_meters) || 500);
                        }} placeholder="34.0195" />
                    </div>
                    <div style={{ ...S.field, flex: 1 }}>
                      <label style={S.label}>Longitude</label>
                      <input className="erm-input" style={S.input} type="number" step="any" value={f.center_longitude}
                        onChange={e => {
                          sf({ center_longitude: e.target.value });
                          const m = mapRef.current;
                          if (m && f.center_latitude && e.target.value) placeMarkerAndCircle(m, parseFloat(e.target.value), parseFloat(f.center_latitude), parseInt(f.radius_meters) || 500);
                        }} placeholder="-118.4912" />
                    </div>
                    <div style={{ ...S.field, flex: 1 }}>
                      <label style={S.label}>Radius (m)</label>
                      <input className="erm-input" style={S.input} type="number" min="10" value={f.radius_meters}
                        onChange={e => sf({ radius_meters: e.target.value })} placeholder="500" />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Step 2: Function & Parameters ── */}
          {step === 2 && (
            <div>
              <div style={S.field}>
                <label style={S.label}>Function *</label>
                {contractFunctions.length > 0 ? (
                  <select
                    className="erm-select"
                    style={S.select}
                    value={f.function_name}
                    onChange={e => handleFunctionSelect(e.target.value)}
                  >
                    <option value="">Select a function…</option>
                    {contractFunctions.map((fn: any, i) => {
                      const fnName: string = fn.name || fn;
                      const paramCount = (fn.parameters || fn.params || []).length;
                      const isRO = READ_ONLY_PREFIXES.some(p => fnName.toLowerCase().startsWith(p));
                      return (
                        <option key={i} value={fnName}>
                          {isRO ? '🔍 ' : ''}{fnName}{paramCount > 0 ? ` (${paramCount} params)` : ' — no params'}
                        </option>
                      );
                    })}
                  </select>
                ) : (
                  <input className="erm-input" style={S.input} value={f.function_name} onChange={e => sf({ function_name: e.target.value })} placeholder="function_name" />
                )}
              </div>

              {/* ── Readonly function banner + simulate button ── */}
              {isReadOnlyFn && (
                <>
                  <div style={{ ...S.infoBox('blue'), marginBottom: '10px', lineHeight: '1.6' }}>
                    <strong>🔍 Read-only function detected.</strong>{' '}
                    GeoLink simulates this call — no wallet signature required, no transaction on-chain.
                    The result is returned immediately.
                  </div>

                  {/* Simulate / Test button */}
                  {form.contract_id && (
                    <div style={{ marginBottom: '14px' }}>
                      <button
                        style={{
                          ...S.btn('primary', !simulating),
                          fontSize: '12px', padding: '7px 16px', marginBottom: '8px',
                        }}
                        onClick={handleSimulate}
                        disabled={simulating}
                      >
                        {simulating ? '⟳ Simulating…' : '▶ Test / Simulate Now'}
                      </button>

                      {/* Simulation error */}
                      {simError && (
                        <div style={{
                          padding: '8px 12px', borderRadius: '6px',
                          background: 'rgba(220,53,69,0.1)', border: '1px solid rgba(220,53,69,0.3)',
                          color: '#dc3545', fontSize: '11px',
                        }}>
                          ⚠ {simError}
                        </div>
                      )}

                      {/* Simulation result */}
                      {simResult && (
                        <div style={{
                          background: '#f8f9fa', border: `1px solid #ddd`,
                          borderLeft: `3px solid ${simResult.success !== false ? '#28a745' : '#dc3545'}`,
                          borderRadius: '6px', padding: '10px 12px',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                            <span style={{
                              fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px',
                              color: simResult.success !== false ? '#28a745' : '#dc3545',
                            }}>
                              {simResult.success !== false ? '✓ Simulation Result' : '✕ Simulation Failed'}
                            </span>
                            {simResult.is_simulation && (
                              <span style={{
                                fontSize: '9px', background: 'rgba(0,123,255,0.1)', color: '#007bff',
                                borderRadius: '3px', padding: '1px 5px', fontWeight: 600,
                              }}>
                                READ-ONLY · NOT ON-CHAIN
                              </span>
                            )}
                          </div>

                          {simResult.function_name && (
                            <div style={{ fontSize: '11px', color: '#555', marginBottom: '6px' }}>
                              <code style={{ fontFamily: 'monospace', color: '#333' }}>{simResult.function_name}</code>
                            </div>
                          )}

                          {simResult.error && (
                            <div style={{ fontSize: '11px', color: '#dc3545', marginBottom: '6px' }}>
                              {simResult.error}
                            </div>
                          )}

                          {simResult.message && (
                            <div style={{ fontSize: '11px', color: '#555', marginBottom: '6px' }}>
                              {simResult.message}
                            </div>
                          )}

                          {simResult.result !== undefined && simResult.result !== null && (
                            <div>
                              <div style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '4px' }}>
                                Return Value
                              </div>
                              <pre style={{
                                background: '#fff', border: '1px solid #eee',
                                borderRadius: '5px', padding: '8px 10px',
                                fontSize: '11px', color: '#333', margin: 0,
                                overflow: 'auto', maxHeight: '160px',
                                fontFamily: 'monospace', lineHeight: '1.5',
                              }}>
                                {typeof simResult.result === 'object'
                                  ? JSON.stringify(simResult.result, null, 2)
                                  : String(simResult.result)}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* ── Parameter reference card ── */}
              {selectedFunctionDef && (() => {
                const params: any[] = selectedFunctionDef.parameters || selectedFunctionDef.params || [];
                return params.length > 0 ? (
                  <div style={{ background: '#060f1c', border: `1px solid ${C.borderFaint}`, borderRadius: '8px', padding: '11px 13px', marginBottom: '14px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: C.textMuted, marginBottom: '8px', letterSpacing: '0.4px', textTransform: 'uppercase' as const }}>
                      Parameters — {selectedFunctionDef.name}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '5px' }}>
                      {params.map((p: any, i: number) => {
                        const pName = p.name ?? p;
                        const pType = p.type ?? p.param_type ?? '?';
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: '8px', fontSize: '12px' }}>
                            <code style={{ color: C.text, fontFamily: 'monospace', minWidth: '160px', flexShrink: 0 }}>{pName}</code>
                            <span style={{ fontSize: '10px', fontWeight: 700, color: typeColor(pType), background: `${typeColor(pType)}18`, padding: '1px 7px', borderRadius: '4px', fontFamily: 'monospace' }}>
                              {pType}
                            </span>
                            {/* DeFindex-specific hints */}
                            {pName === 'caller' && <span style={{ fontSize: '10px', color: C.textFaint }}>← user's wallet address</span>}
                            {pName === 'roles' && <span style={{ fontSize: '10px', color: C.textFaint }}>← {'{'}manager, fee_receiver{'}'} map</span>}
                            {pName === 'vault_fee' && <span style={{ fontSize: '10px', color: C.textFaint }}>← basis points (100 = 1%)</span>}
                            {pName === 'assets' && <span style={{ fontSize: '10px', color: C.textFaint }}>← array of {'{'}address, ratio{'}'} objects</span>}
                            {pName === 'name_symbol' && <span style={{ fontSize: '10px', color: C.textFaint }}>← {'{'}name, symbol{'}'} map</span>}
                            {pName === 'amounts' && <span style={{ fontSize: '10px', color: C.textFaint }}>← initial deposit per asset (stroops)</span>}
                            {pName === 'soroswap_router' && <span style={{ fontSize: '10px', color: C.textFaint }}>← Soroswap router contract address</span>}
                            {pName === 'upgradable' && <span style={{ fontSize: '10px', color: C.textFaint }}>← true/false</span>}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: '10px', color: C.textFaint, marginTop: '8px', borderTop: `1px solid ${C.borderFaint}`, paddingTop: '7px' }}>
                      Template auto-scaffolded below — replace placeholder values with real ones.
                    </div>
                  </div>
                ) : (
                  <div style={{ ...S.infoBox('blue'), fontSize: '11px' }}>
                    This function takes no parameters.
                  </div>
                );
              })()}

              <div style={S.field}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                  <label style={S.label}>Parameters (JSON)</label>
                  {selectedFunctionDef && (
                    <button
                      style={{ ...S.btn('ghost', true), fontSize: '10px', color: C.indigoLight, padding: '2px 8px' }}
                      onClick={() => {
                        const template = buildParamTemplate(selectedFunctionDef);
                        sf({ function_parameters: JSON.stringify(template, null, 2) });
                      }}
                      title="Reset to scaffolded template"
                    >
                      ↺ Reset template
                    </button>
                  )}
                </div>
                <textarea
                  className="erm-textarea"
                  style={{ ...S.textarea, minHeight: '160px' }}
                  value={f.function_parameters}
                  onChange={e => sf({ function_parameters: e.target.value })}
                  rows={8}
                  placeholder={'{\n  "amount": 10000000,\n  "asset": "XLM"\n}'}
                />
                <div style={{ display: 'flex', gap: '12px', marginTop: '5px', flexWrap: 'wrap' as const }}>
                  <div style={S.hint}>Amount / stroops: 1 XLM = 10,000,000</div>
                  {(() => {
                    try { JSON.parse(f.function_parameters); return <div style={{ fontSize: '11px', color: C.green }}>✓ valid JSON</div>; }
                    catch { return f.function_parameters.trim() ? <div style={{ fontSize: '11px', color: C.red }}>⚠ invalid JSON</div> : null; }
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* ── Step 3: Advanced ── */}
          {step === 3 && (
            <div>
              {/* Trigger & Execution */}
              <div style={S.field}>
                <label style={S.label}>Trigger On</label>
                <select className="erm-select" style={S.select} value={f.trigger_on} onChange={e => sf({ trigger_on: e.target.value as any, min_location_duration_seconds: e.target.value !== 'dwell' ? '' : f.min_location_duration_seconds })}>
                  <option value="enter">On Enter — fires when wallet enters the zone</option>
                  <option value="exit">On Exit — fires when wallet leaves the zone</option>
                  <option value="dwell">After Dwell — fires after wallet stays in zone for min duration</option>
                </select>
              </div>

              {/* Dwell time — only shown when trigger_on = dwell */}
              {f.trigger_on === 'dwell' && (
                <div style={{ paddingLeft: '8px', borderLeft: `3px solid ${C.indigo}`, marginBottom: '14px' }}>
                  <div style={S.field}>
                    <label style={S.label}>Minimum Dwell Time (seconds) *</label>
                    <input className="erm-input" style={S.input} type="number" min="1" value={f.min_location_duration_seconds}
                      onChange={e => sf({ min_location_duration_seconds: e.target.value })} placeholder="e.g. 30" />
                    <div style={S.hint}>The wallet must stay inside the zone continuously for this many seconds. If it leaves and re-enters, the timer resets.</div>
                  </div>
                  {/* Quick presets */}
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' as const, marginTop: '-6px', marginBottom: '10px' }}>
                    {[['30s', '30'], ['5 min', '300'], ['30 min', '1800'], ['1 hr', '3600'], ['24 hr', '86400']].map(([label, val]) => (
                      <button key={val} style={{ ...S.btn('secondary', true), fontSize: '10px', padding: '3px 10px' }}
                        onClick={() => sf({ min_location_duration_seconds: val })}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <Toggle on={f.auto_execute} onChange={v => sf({ auto_execute: v })}
                label="Auto Execute" sub="Run contract function automatically without user prompt" />
              <Toggle on={f.requires_confirmation} onChange={v => sf({ requires_confirmation: v })}
                label="Requires Confirmation" sub="User must confirm before execution" />

              {/* Rate Limiting */}
              <div style={S.sectionDivider}>
                <div style={S.sectionTitle}>Rate Limiting (optional)</div>
              </div>
              <div style={S.infoBox('blue')}>Limit how many times a wallet can trigger this rule within a time window. Counter only increments on executed deposits.</div>
              {/* Presets */}
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' as const, marginBottom: '10px' }}>
                {[
                  ['Once / hour',   '1', '3600'],
                  ['Once / day',    '1', '86400'],
                  ['Once / week',   '1', '604800'],
                  ['Unlimited',     '',  ''],
                ].map(([label, maxEx, window]) => (
                  <button key={label} style={{
                    ...S.btn('secondary', true), fontSize: '10px', padding: '4px 12px',
                    background: f.max_executions_per_public_key === maxEx && f.execution_time_window_seconds === window ? C.indigo : '#f0f0f0',
                    color: f.max_executions_per_public_key === maxEx && f.execution_time_window_seconds === window ? '#000' : C.text,
                  }}
                    onClick={() => sf({ max_executions_per_public_key: maxEx, execution_time_window_seconds: window })}>
                    {label}
                  </button>
                ))}
              </div>
              <div style={S.row}>
                <div style={{ ...S.field, flex: 1 }}>
                  <label style={S.label}>Max Executions per Wallet</label>
                  <input className="erm-input" style={S.input} type="number" min="1" value={f.max_executions_per_public_key}
                    onChange={e => sf({ max_executions_per_public_key: e.target.value })} placeholder="leave empty = unlimited" />
                </div>
                <div style={{ ...S.field, flex: 1 }}>
                  <label style={S.label}>Time Window (seconds)</label>
                  <input className="erm-input" style={S.input} type="number" min="1" value={f.execution_time_window_seconds}
                    onChange={e => sf({ execution_time_window_seconds: e.target.value })} placeholder="e.g. 3600 (1 hr)" />
                </div>
              </div>
              {/* Cross-field validation hint */}
              {(f.max_executions_per_public_key && !f.execution_time_window_seconds) && (
                <div style={{ fontSize: '11px', color: C.amber, marginTop: '-8px', marginBottom: '10px' }}>⚠ Set a time window when limiting executions</div>
              )}
              {(f.execution_time_window_seconds && !f.max_executions_per_public_key) && (
                <div style={{ fontSize: '11px', color: C.amber, marginTop: '-8px', marginBottom: '10px' }}>⚠ Set max executions when specifying a time window</div>
              )}

              {/* Target Wallet */}
              <div style={{ ...S.sectionDivider }}>
                <div style={S.sectionTitle}>Target Wallet</div>
              </div>
              <div style={S.field}>
                <label style={S.label}>Target Wallet Public Key (optional)</label>
                <input className="erm-input" style={S.input} value={f.target_wallet_public_key} onChange={e => sf({ target_wallet_public_key: e.target.value })}
                  placeholder="Leave empty to apply to any wallet" />
                <div style={S.hint}>Restrict this rule to a specific wallet address</div>
              </div>

              {/* Multi-Wallet Quorum */}
              <div style={S.sectionDivider}>
                <div style={S.sectionTitle}>Multi-Wallet Quorum (optional)</div>
              </div>
              <div style={S.infoBox('blue')}>Require multiple wallets to be co-present before execution triggers — e.g. customer + merchant.</div>
              <div style={S.field}>
                <label style={S.label}>Required Wallet Public Keys</label>
                <textarea className="erm-textarea" style={{ ...S.textarea, minHeight: '60px' }} value={f.required_wallet_public_keys}
                  onChange={e => sf({ required_wallet_public_keys: e.target.value })}
                  placeholder="GDABC..., GDXYZ... (comma-separated)" rows={2} />
              </div>
              <div style={S.row}>
                <div style={{ ...S.field, flex: 1 }}>
                  <label style={S.label}>Minimum Wallet Count</label>
                  <input className="erm-input" style={S.input} type="number" min="1" value={f.minimum_wallet_count}
                    onChange={e => sf({ minimum_wallet_count: e.target.value })} placeholder="e.g. 2" />
                </div>
                <div style={{ ...S.field, flex: 1 }}>
                  <label style={S.label}>Quorum Mode</label>
                  <select className="erm-select" style={S.select} value={f.quorum_type} onChange={e => sf({ quorum_type: e.target.value as any })}>
                    <option value="any">At least N wallets present</option>
                    <option value="all">All listed wallets present</option>
                  </select>
                </div>
              </div>

              {/* Read-only ledger — always shown so users understand the option */}
              <div style={S.sectionDivider}>
                <div style={S.sectionTitle}>Ledger Submission</div>
              </div>
              {isReadOnlyFn ? (
                <div style={{ ...S.infoBox('blue'), marginBottom: '10px', fontSize: '11px', lineHeight: '1.6' }}>
                  <strong>ℹ️ This is a read-only function.</strong> GeoLink simulates it automatically — no
                  on-chain transaction by default. Enable the toggle below <em>only</em> if you need the
                  simulation result posted to the Stellar ledger for auditing. Requires a server-side signing key.
                </div>
              ) : (
                <div style={{ ...S.infoBox('amber'), marginBottom: '10px', fontSize: '11px', lineHeight: '1.6' }}>
                  <strong>ℹ️ This is a write function.</strong> It will execute a real Stellar transaction.
                  The toggle below applies only to read-only functions (names starting with get_, is_, has_, etc.).
                </div>
              )}
              <Toggle on={f.submit_readonly_to_ledger} onChange={v => sf({ submit_readonly_to_ledger: v })}
                label="Submit Read-Only Result to Ledger"
                sub="Post the simulation result as a real Stellar transaction (requires server signing key)" />

              {/* Auto-deactivation */}
              <div style={S.sectionDivider}>
                <div style={S.sectionTitle}>Auto-Deactivation (optional)</div>
              </div>
              <div style={S.infoBox('amber')}>⚠ Automatically deactivate this rule when balance drops below a threshold.</div>
              <Toggle on={f.auto_deactivate_on_balance_threshold} onChange={v => sf({ auto_deactivate_on_balance_threshold: v })}
                label="Auto-Deactivate on Low Balance"
                sub="Disable rule when balance drops below threshold" />
              {f.auto_deactivate_on_balance_threshold && (
                <div style={{ paddingLeft: '8px', borderLeft: `2px solid ${C.border}`, marginTop: '8px' }}>
                  <div style={S.row}>
                    <div style={{ ...S.field, flex: 1 }}>
                      <label style={S.label}>Balance Threshold (XLM) *</label>
                      <input className="erm-input" style={S.input} type="number" min="0" step="0.0000001" value={f.balance_threshold_xlm}
                        onChange={e => sf({ balance_threshold_xlm: e.target.value })} placeholder="e.g. 10" />
                    </div>
                    <div style={{ ...S.field, flex: 1 }}>
                      <label style={S.label}>Asset Address (optional)</label>
                      <input className="erm-input" style={S.input} value={f.balance_check_asset_address}
                        onChange={e => sf({ balance_check_asset_address: e.target.value })}
                        placeholder="Empty = native XLM" />
                    </div>
                  </div>
                  <Toggle on={f.use_smart_wallet_balance} onChange={v => sf({ use_smart_wallet_balance: v })}
                    label="Use Smart Wallet Vault Balance"
                    sub="Check vault balance instead of direct wallet balance" />
                </div>
              )}
            </div>
          )}

          {/* ── Step 4: Review ── */}
          {step === 4 && (
            <div>
              <div style={{ fontSize: '12px', color: C.textFaint, marginBottom: '14px' }}>Review your rule configuration before saving.</div>
              {[
                ['Rule Name', f.rule_name],
                ['Contract', selectedContract?.contract_name || f.contract_id],
                ['Rule Type', f.rule_type],
                ...(f.rule_type !== 'geofence' ? [
                  ['Location', `${parseFloat(f.center_latitude || '0').toFixed(4)}, ${parseFloat(f.center_longitude || '0').toFixed(4)}`],
                  ['Radius', `${f.radius_meters} m`],
                ] : f.geofence_input_mode === 'id' ? [
                  ['Geofence ID', f.geofence_id],
                ] : f.geofence_input_mode === 'country' ? [
                  ['Country', f.selected_country_name],
                  ['Boundary', (() => { try { const g = JSON.parse(f.geojson_geometry); return `${g.type} (from countries.geojson) · ${formatArea(geometryAreaSqMeters(g))}`; } catch { return '—'; } })()],
                ] : [
                  ['GeoJSON', (() => { try { const g = JSON.parse(f.geojson_geometry); return `${g.type} (inline) · ${formatArea(geometryAreaSqMeters(g))}`; } catch { return 'Inline GeoJSON'; } })()],
                ]),
                ['Function', `${f.function_name}${isReadOnlyFn ? ' (read-only — simulated)' : ''}`],
                ['Trigger', f.trigger_on],
                ['Auto-execute', f.auto_execute ? '✓ Yes' : 'No'],
                ['Requires confirmation', f.requires_confirmation ? '✓ Yes' : 'No'],
                ...(f.max_executions_per_public_key ? [['Max executions', `${f.max_executions_per_public_key}${f.execution_time_window_seconds ? ` / ${f.execution_time_window_seconds}s window` : ''}`]] : []),
                ...(f.trigger_on === 'dwell' && f.min_location_duration_seconds ? [['Min dwell time', `${f.min_location_duration_seconds}s`]] : []),
                ...(f.submit_readonly_to_ledger ? [['Ledger submission', 'Enabled']] : []),
                ...(f.auto_deactivate_on_balance_threshold ? [['Auto-deactivate threshold', `${f.balance_threshold_xlm} XLM`]] : []),
              ].map(([k, v]) => (
                <div key={k} style={S.reviewRow}>
                  <span style={{ color: C.textFaint }}>{k}</span>
                  <span style={{ color: C.text, textAlign: 'right' as const }}>{v}</span>
                </div>
              ))}
              {error && <div style={{ marginTop: '12px', color: C.red, fontSize: '12px' }}>⚠ {error}</div>}
            </div>
          )}

          </div>
          {/* Nav (sticky footer outside the body scroll container) */}
          <div style={{ ...S.navRow, flexShrink: 0, marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${C.borderFaint}` }}>
            <button style={S.btn('secondary', true)} onClick={step === 0 ? closeWizard : () => setStep(s => s - 1)} disabled={saving}>
              {step === 0 ? 'Cancel' : '← Back'}
            </button>
            {step < STEPS.length - 1 ? (
              <button style={{ ...S.btn('primary', true), opacity: canNext() ? 1 : 0.4 }}
                onClick={() => setStep(s => s + 1)} disabled={!canNext()}>
                Next →
              </button>
            ) : (
              <button style={S.btn('primary', true)} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editingRule ? 'Save Changes' : 'Create Rule'}
              </button>
            )}
          </div>
          </div>
        </div>
      )}

      {/* ── Rules List ── */}
      <div>
        <div style={{ fontSize: '12px', color: C.textFaint, marginBottom: '10px' }}>
          {loading ? 'Loading…' : `${rules.length} rule${rules.length !== 1 ? 's' : ''}`}
        </div>
        {!loading && rules.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 0', color: C.textFaint, fontSize: '13px' }}>
            No execution rules yet.<br />
            <span style={{ color: C.indigoLight, cursor: 'pointer' }} onClick={openCreate}>Create your first rule →</span>
          </div>
        )}
        {rules.map(rule => {
          const authority = authorityFor(rule);
          const owned = mayManageRule(rule);
          // Look up the parent contract so we can surface "paused —
          // parent contract inactive" on rule cards whose parent has
          // been soft-deleted. The rule's own `is_active` stays
          // untouched on contract delete (GeoLink doesn't cascade —
          // see the cascade-design discussion in the inline help
          // panel below), so without this badge the rule looks
          // perfectly armed even though it cannot fire because
          // GeoLink's `/api/contracts/nearby` filters out rules whose
          // parent contract is inactive. The amber badge makes the
          // effective state visible at a glance.
          const parentContract =
            typeof rule.contract_id === 'number'
              ? contracts.find((c) => c.id === rule.contract_id)
              : undefined;
          const parentInactive = parentContract && parentContract.is_active === false;
          return (
          <div key={rule.id} style={S.card}>
            <div style={S.cardHeader}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: C.text, marginBottom: '5px' }}>{rule.rule_name}</div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' as const }}>
                  <span style={S.pill(rule.is_active ? 'green' : 'grey')}>{rule.is_active ? '● Active' : '○ Inactive'}</span>
                  {parentInactive && (
                    <span
                      style={S.pill('amber')}
                      title={
                        'Parent contract is soft-deleted (is_active=false). ' +
                        'This rule will not fire until the contract is reactivated. ' +
                        "Open the Smart Contracts → Registered Contracts tab to flip it back on."
                      }
                    >
                      ⏸ contract paused
                    </span>
                  )}
                  <span style={S.pill('grey')}>{rule.rule_type}</span>
                  {rule.auto_execute && <span style={S.pill('indigo')}>⚡ auto</span>}
                  {rule.submit_readonly_to_ledger && <span style={S.pill('indigo')}>📡 ledger</span>}
                  {/*
                    Why the management controls are absent, in the rule's own
                    terms. This used to say "shared" and mean "someone else
                    created it", which was frequently the wrong reason — the
                    real one is now usually a suspended object, an unapproved
                    binding, or an organization whose authority was withdrawn.
                  */}
                  {!owned && (
                    <span
                      style={S.pill('grey')}
                      title={
                        resolvingAuthority
                          ? 'Checking what this wallet may do with this rule…'
                          : authority?.reason
                            || 'This wallet has no authority to manage this rule.'
                      }
                    >
                      {resolvingAuthority ? '⋯ checking' : '🔒 read only'}
                    </span>
                  )}
                  {/*
                    Named rather than implied: this rule's authority came from
                    stored provenance because no spatial object points at it.
                  */}
                  {authority?.legacy && (
                    <span
                      style={S.pill('amber')}
                      title={
                        'No spatial object links to this rule, so authority falls back to '
                        + 'the wallet recorded as its creator. Link it from a spatial '
                        + 'object to have this decided on chain.'
                      }
                    >
                      creator (legacy)
                    </span>
                  )}
                  {authority?.objectName && !authority.legacy && (
                    <span
                      style={S.pill('indigo')}
                      title={`Authority for this rule resolves through ${authority.objectName}.`}
                    >
                      📍 {authority.objectName}
                    </span>
                  )}
                </div>
              </div>
              {owned && (
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  <button style={S.btn('secondary', true)} onClick={() => handleToggle(rule)} title={rule.is_active ? 'Deactivate' : 'Activate'}>
                    {rule.is_active ? '⏸' : '▶'}
                  </button>
                  <button style={S.btn('secondary', true)} onClick={() => openEdit(rule)} title="Edit">✏️</button>
                  <button style={S.btn('danger', true)} onClick={() => handleDelete(rule)} title="Delete">🗑</button>
                </div>
              )}
            </div>
            <div style={S.infoRow}><strong style={{ color: C.textMuted }}>Contract:</strong> {rule.contract_name || `#${rule.contract_id}`}</div>
            <div style={S.infoRow}><strong style={{ color: C.textMuted }}>Function:</strong> <code style={{ fontSize: '11px', color: C.indigoLight }}>{rule.function_name}</code></div>
            {rule.center_latitude != null && (
              <div style={S.infoRow}>
                <strong style={{ color: C.textMuted }}>Location:</strong> {Number(rule.center_latitude).toFixed(4)}, {Number(rule.center_longitude).toFixed(4)}
                {rule.radius_meters && <span style={{ color: C.textFaint }}> · {rule.radius_meters}m radius</span>}
              </div>
            )}
            <div style={S.infoRow}><strong style={{ color: C.textMuted }}>Trigger:</strong> on {rule.trigger_on || 'enter'}</div>
          </div>
          );
        })}
      </div>
    </div>
  );
};
